/**
 * lib/ninetailed/mapAudiences.ts
 *
 * Pure mapper: turns the Contentful GraphQL `ntAudienceCollection` shape into the
 * `ExposedAudienceDefinition[]` (`{ id, name, description? }`) the Ninetailed
 * preview widget lists in its Audiences panel.
 *
 * Defensive by design (mirrors `mapExperiences`): Contentful data can be partially
 * authored or malformed, so on ANY unexpected input this returns `[]` and NEVER
 * throws. An audience is skipped unless it has both an id and a name (the two
 * fields the widget needs to render a row). Preview-only — never on the render path.
 */

import type { ExposedAudienceDefinition } from '@ninetailed/experience.js-preview-bridge';

type RawAudience = {
  ntAudienceId?: string | null;
  ntName?: string | null;
  ntDescription?: string | null;
} | null;

function toItemsArray(input: unknown): Array<RawAudience> | null {
  if (Array.isArray(input)) return input as Array<RawAudience>;
  if (input && Array.isArray((input as { items?: unknown }).items)) {
    return (input as { items: Array<RawAudience> }).items;
  }
  return null;
}

/**
 * Map the raw GraphQL audiences collection to `ExposedAudienceDefinition[]`.
 * Returns `[]` for any missing/malformed input and never throws.
 */
export function mapAudiences(input: unknown): ExposedAudienceDefinition[] {
  try {
    const items = toItemsArray(input);
    if (!items) return [];

    return items.reduce<ExposedAudienceDefinition[]>((acc, raw) => {
      if (
        raw &&
        typeof raw.ntAudienceId === 'string' &&
        typeof raw.ntName === 'string'
      ) {
        acc.push({
          id: raw.ntAudienceId,
          name: raw.ntName,
          description:
            typeof raw.ntDescription === 'string'
              ? raw.ntDescription
              : undefined,
        });
      }
      return acc;
    }, []);
  } catch {
    return [];
  }
}
